import React, { useState, useEffect } from 'react';
import { 
  Trophy, Calendar, MapPin, Users, Gift, AlertCircle, 
  Shield, Lock, FileText, CheckCircle, ArrowLeft, Clock
} from 'lucide-react';
import './BHyyve1000.css';

const POLLEN_IMG = "https://customer-assets.emergentagent.com/job_bee-booking/artifacts/k8zgjyqb_Pollen%20-%20bHyyve%20Mascot.png";

// Contest end date: July 18, 2026 at 11:59 PM EST
const CONTEST_END_DATE = new Date('2026-07-18T23:59:59-05:00');

const BHyyve1000 = () => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    isExpired: false
  });

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const difference = CONTEST_END_DATE - now;

      if (difference <= 0) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0, isExpired: true };
      }

      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / (1000 * 60)) % 60),
        seconds: Math.floor((difference / 1000) % 60),
        isExpired: false
      };
    };

    // Initial calculation
    setTimeLeft(calculateTimeLeft());

    // Update every second
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="bh1000-page" data-testid="bhyyve1000-page">
      {/* Header */}
      <header className="bh1000-header">
        <div className="bh1000-container">
          <a href="/" className="bh1000-logo-link">
            <img src="/logo.svg" alt="bHyyve" className="bh1000-logo" />
            <span className="bh1000-logo-text">bHyyve</span>
          </a>
          <a href="/" className="bh1000-back-link">
            <ArrowLeft size={18} />
            Back to Home
          </a>
        </div>
      </header>

      {/* Countdown Timer Banner */}
      <div className="bh1000-countdown-banner" data-testid="countdown-timer">
        <div className="bh1000-container">
          <div className="bh1000-countdown-content">
            <div className="bh1000-countdown-label">
              <Clock size={20} />
              <span>{timeLeft.isExpired ? 'Contest Ended' : 'Time Remaining to Enter'}</span>
            </div>
            {!timeLeft.isExpired ? (
              <div className="bh1000-countdown-timer">
                <div className="bh1000-countdown-unit">
                  <span className="bh1000-countdown-value">{String(timeLeft.days).padStart(2, '0')}</span>
                  <span className="bh1000-countdown-text">Days</span>
                </div>
                <span className="bh1000-countdown-separator">:</span>
                <div className="bh1000-countdown-unit">
                  <span className="bh1000-countdown-value">{String(timeLeft.hours).padStart(2, '0')}</span>
                  <span className="bh1000-countdown-text">Hours</span>
                </div>
                <span className="bh1000-countdown-separator">:</span>
                <div className="bh1000-countdown-unit">
                  <span className="bh1000-countdown-value">{String(timeLeft.minutes).padStart(2, '0')}</span>
                  <span className="bh1000-countdown-text">Mins</span>
                </div>
                <span className="bh1000-countdown-separator">:</span>
                <div className="bh1000-countdown-unit">
                  <span className="bh1000-countdown-value bh1000-seconds">{String(timeLeft.seconds).padStart(2, '0')}</span>
                  <span className="bh1000-countdown-text">Secs</span>
                </div>
              </div>
            ) : (
              <div className="bh1000-countdown-expired">
                The entry period has ended. Winner will be announced soon!
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="bh1000-hero">
        <div className="bh1000-container">
          <div className="bh1000-hero-content">
            <div className="bh1000-hero-text">
              <div className="bh1000-badge">
                <Trophy size={18} />
                <span>Official Contest</span>
              </div>
              <h1 className="bh1000-title">
                <span className="bh1000-prize">$1,000</span>
                <span className="bh1000-giveaway">Giveaway</span>
              </h1>
              <p className="bh1000-subtitle">
                Enter for a chance to win $1,000 CAD! No purchase necessary.
              </p>
              
              <div className="bh1000-quick-info">
                <div className="bh1000-info-item">
                  <Calendar size={20} />
                  <div>
                    <span className="bh1000-info-label">Entry Period</span>
                    <span className="bh1000-info-value">Feb 18 - July 18, 2026</span>
                  </div>
                </div>
                <div className="bh1000-info-item">
                  <MapPin size={20} />
                  <div>
                    <span className="bh1000-info-label">Eligible Regions</span>
                    <span className="bh1000-info-value">GTA, Hamilton, Niagara</span>
                  </div>
                </div>
                <div className="bh1000-info-item">
                  <Users size={20} />
                  <div>
                    <span className="bh1000-info-label">Age Requirement</span>
                    <span className="bh1000-info-value">18+ years old</span>
                  </div>
                </div>
              </div>

              <div className="bh1000-how-to-enter">
                <h3>How to Enter</h3>
                <p>Comment <strong>"bHyyve"</strong> on our official giveaway post on Facebook during the Entry Period.</p>
              </div>
            </div>
            <div className="bh1000-hero-visual">
              <img src={POLLEN_IMG} alt="Pollen - bHyyve Mascot" className="bh1000-pollen" />
              <div className="bh1000-prize-badge">
                <span className="bh1000-prize-amount">$1,000</span>
                <span className="bh1000-prize-currency">CAD</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Terms & Conditions */}
      <section className="bh1000-terms">
        <div className="bh1000-container">
          <div className="bh1000-terms-header">
            <FileText size={32} />
            <h2>Official Terms, Conditions & Eligibility</h2>
          </div>

          <div className="bh1000-terms-content">
            {/* Section 1 */}
            <div className="bh1000-term-section">
              <div className="bh1000-term-number">1</div>
              <div className="bh1000-term-content">
                <h3>Sponsor</h3>
                <p>This giveaway is sponsored and administered by bHyyve Technologies Inc. ("Sponsor").</p>
              </div>
            </div>

            {/* Section 2 */}
            <div className="bh1000-term-section bh1000-highlight">
              <div className="bh1000-term-number">2</div>
              <div className="bh1000-term-content">
                <h3>
                  <AlertCircle size={18} />
                  No Affiliation with Facebook
                </h3>
                <p>This promotion is in no way sponsored, endorsed, administered by, or associated with Facebook. By entering, participants release Facebook from any responsibility related to this giveaway.</p>
              </div>
            </div>

            {/* Section 3 */}
            <div className="bh1000-term-section">
              <div className="bh1000-term-number">3</div>
              <div className="bh1000-term-content">
                <h3>Eligibility</h3>
                <ul className="bh1000-eligibility-list">
                  <li>
                    <CheckCircle size={16} />
                    <span>Open to legal residents of Greater Toronto Area, Hamilton and Niagara.</span>
                  </li>
                  <li>
                    <CheckCircle size={16} />
                    <span>Must be at least 18 years old at the time of entry.</span>
                  </li>
                  <li>
                    <CheckCircle size={16} />
                    <span>Void where prohibited by law.</span>
                  </li>
                  <li>
                    <CheckCircle size={16} />
                    <span>Employees, affiliates, and immediate family members of the Sponsor are not eligible.</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Section 4 */}
            <div className="bh1000-term-section">
              <div className="bh1000-term-number">4</div>
              <div className="bh1000-term-content">
                <h3>
                  <Calendar size={18} />
                  Entry Period
                </h3>
                <p>The giveaway begins on <strong>February 18th, 2026</strong> and ends on <strong>July 18th, 2026</strong> ("Entry Period"). Entries submitted outside this period will not be eligible.</p>
              </div>
            </div>

            {/* Section 5 */}
            <div className="bh1000-term-section bh1000-highlight">
              <div className="bh1000-term-number">5</div>
              <div className="bh1000-term-content">
                <h3>How to Enter</h3>
                <p>To enter, participants must:</p>
                <ul className="bh1000-entry-list">
                  <li>
                    <span className="bh1000-step">1</span>
                    <span>Comment <strong>"bHyyve"</strong> on the official giveaway post during the Entry Period.</span>
                  </li>
                  <li>
                    <span className="bh1000-step">2</span>
                    <span>Limit one entry per person/account. Multiple accounts or duplicate entries may result in disqualification.</span>
                  </li>
                </ul>
                <div className="bh1000-no-purchase">
                  <Gift size={18} />
                  <span>No purchase is necessary to enter or win.</span>
                </div>
              </div>
            </div>

            {/* Section 6 */}
            <div className="bh1000-term-section">
              <div className="bh1000-term-number">6</div>
              <div className="bh1000-term-content">
                <h3>
                  <Trophy size={18} />
                  Prize
                </h3>
                <ul className="bh1000-prize-details">
                  <li>One (1) winner will receive <strong>$1,000 CAD</strong>.</li>
                  <li>Prize is non-transferable.</li>
                  <li>No substitution except at Sponsor's discretion.</li>
                  <li>Winner is responsible for any applicable taxes or reporting requirements.</li>
                </ul>
              </div>
            </div>

            {/* Section 7 */}
            <div className="bh1000-term-section">
              <div className="bh1000-term-number">7</div>
              <div className="bh1000-term-content">
                <h3>Winner Selection & Notification</h3>
                <ul className="bh1000-winner-list">
                  <li>Winner will be selected at random from all eligible entries.</li>
                  <li>Winner will be contacted via Facebook within 7 days after the Entry Period ends.</li>
                  <li>If the winner does not respond within 48 hours, Sponsor may select an alternate winner.</li>
                </ul>
              </div>
            </div>

            {/* Section 8 */}
            <div className="bh1000-term-section">
              <div className="bh1000-term-number">8</div>
              <div className="bh1000-term-content">
                <h3>Publicity Consent</h3>
                <p>By entering, participants agree that Sponsor may use the winner's name and likeness for promotional purposes without additional compensation, unless prohibited by law.</p>
              </div>
            </div>

            {/* Section 9 */}
            <div className="bh1000-term-section">
              <div className="bh1000-term-number">9</div>
              <div className="bh1000-term-content">
                <h3>
                  <AlertCircle size={18} />
                  Disqualification
                </h3>
                <p>Sponsor reserves the right to disqualify any entry that is incomplete, fraudulent, automated, or violates Facebook rules or these terms.</p>
              </div>
            </div>

            {/* Section 10 */}
            <div className="bh1000-term-section">
              <div className="bh1000-term-number">10</div>
              <div className="bh1000-term-content">
                <h3>
                  <Shield size={18} />
                  Limitation of Liability
                </h3>
                <p>Sponsor is not responsible for:</p>
                <ul>
                  <li>Technical failures</li>
                  <li>Lost or delayed entries</li>
                  <li>Unauthorized interference</li>
                  <li>Any injury or damage related to participation or prize use</li>
                </ul>
              </div>
            </div>

            {/* Section 11 */}
            <div className="bh1000-term-section">
              <div className="bh1000-term-number">11</div>
              <div className="bh1000-term-content">
                <h3>
                  <Lock size={18} />
                  Privacy
                </h3>
                <p>Information collected is used solely for administering the giveaway and will not be sold or shared except as required by law.</p>
              </div>
            </div>

            {/* Section 12 */}
            <div className="bh1000-term-section">
              <div className="bh1000-term-number">12</div>
              <div className="bh1000-term-content">
                <h3>General Conditions</h3>
                <ul>
                  <li>Sponsor reserves the right to cancel, modify, or suspend the giveaway if fraud, technical failures, or other factors impair the integrity of the promotion.</li>
                  <li><strong>Participation constitutes acceptance of these Terms & Conditions.</strong></li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bh1000-footer">
        <div className="bh1000-container">
          <p>© 2026 bHyyve Technologies Inc. All rights reserved.</p>
          <p className="bh1000-footer-links">
            <a href="/privacy">Privacy Policy</a>
            <span>•</span>
            <a href="/terms">Terms of Service</a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default BHyyve1000;
